import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProspectChildren, createProspect } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Building2, ChevronRight, FolderKanban, Plus } from 'lucide-react';
import { toast } from 'sonner';

export const CoproprietesSection = ({ parentProspect, canEdit }) => {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        nom_copropriete: '',
        adresse: '',
        ville: '',
        code_postal: '',
        nom_referent: '',
        prenom_referent: '',
        telephone: '',
        email: '',
        nature_projet: ''
    });

    const load = async () => {
        setLoading(true);
        try {
            const res = await getProspectChildren(parentProspect.id);
            setChildren(res.data || []);
        } catch {
            setChildren([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [parentProspect.id]);

    const handleCreate = async () => {
        if (!form.nom_copropriete) {
            toast.error('Le nom de la copropriété est obligatoire');
            return;
        }
        if (!form.telephone) {
            toast.error('Un téléphone de contact est obligatoire');
            return;
        }
        setSubmitting(true);
        try {
            await createProspect({
                parent_prospect_id: parentProspect.id,
                nom_copropriete: form.nom_copropriete,
                // Required prospect fields - use referent or syndic data
                nom: form.nom_referent || parentProspect.nom,
                prenom: form.prenom_referent || form.nom_copropriete,
                societe: form.nom_copropriete,
                telephone: form.telephone,
                email: form.email || undefined,
                adresse: form.adresse,
                ville: form.ville,
                code_postal: form.code_postal,
                type_contact: 'syndic',
                source_lead: parentProspect.source_lead || 'autre',
                nature_projet: form.nature_projet,
                commercial_id: parentProspect.commercial_id
            });
            toast.success('Copropriété ajoutée');
            setOpen(false);
            setForm({
                nom_copropriete: '',
                adresse: '',
                ville: '',
                code_postal: '',
                nom_referent: '',
                prenom_referent: '',
                telephone: '',
                email: '',
                nature_projet: ''
            });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white border border-[#E4E4E7] p-6" data-testid="coproprietes-section">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#7C3AED]" />
                    <h2 className="text-lg font-semibold text-[#0A0A0A]">
                        Copropriétés ({children.length})
                    </h2>
                </div>
                {canEdit && (
                    <Button
                        onClick={() => setOpen(true)}
                        className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm"
                        data-testid="add-copropriete-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter une copropriété
                    </Button>
                )}
            </div>

            {loading ? (
                <p className="text-sm text-[#52525B]">Chargement...</p>
            ) : children.length === 0 ? (
                <div className="text-center py-8 text-[#52525B]">
                    <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune copropriété rattachée</p>
                    {canEdit && <p className="text-sm mt-1">Cliquez sur "Ajouter" pour créer la première</p>}
                </div>
            ) : (
                <div className="divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-sm">
                    {children.map((c) => (
                        <Link
                            key={c.id}
                            to={`/prospects/${c.id}`}
                            className="flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors"
                            data-testid={`copro-${c.id}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-sm bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                                    <Building2 className="w-5 h-5 text-[#7C3AED]" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium text-[#0A0A0A] truncate">
                                        {c.nom_copropriete || c.societe || `${c.prenom} ${c.nom}`}
                                    </p>
                                    <p className="text-xs text-[#52525B] truncate">
                                        {c.adresse} {c.ville && `· ${c.ville}`}
                                        {c.dossiers_count > 0 && (
                                            <span className="ml-2 inline-flex items-center gap-1">
                                                · <FolderKanban className="w-3 h-3" /> {c.dossiers_count} dossier{c.dossiers_count > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-[#52525B] shrink-0" />
                        </Link>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="copropriete-dialog">
                    <DialogHeader>
                        <DialogTitle>Ajouter une copropriété</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                        <div>
                            <Label className="field-label">Nom de la copropriété *</Label>
                            <Input
                                value={form.nom_copropriete}
                                onChange={(e) => setForm((p) => ({ ...p, nom_copropriete: e.target.value }))}
                                placeholder="Ex : Résidence Les Tilleuls"
                                className="h-10 rounded-sm mt-1"
                                data-testid="copro-nom-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Prénom référent</Label>
                                <Input
                                    value={form.prenom_referent}
                                    onChange={(e) => setForm((p) => ({ ...p, prenom_referent: e.target.value }))}
                                    placeholder="Prénom"
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Nom référent</Label>
                                <Input
                                    value={form.nom_referent}
                                    onChange={(e) => setForm((p) => ({ ...p, nom_referent: e.target.value }))}
                                    placeholder="Nom"
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Téléphone *</Label>
                                <Input
                                    value={form.telephone}
                                    onChange={(e) => setForm((p) => ({ ...p, telephone: e.target.value }))}
                                    placeholder="06 12 34 56 78"
                                    className="h-10 rounded-sm mt-1"
                                    data-testid="copro-tel-input"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                    placeholder="contact@..."
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Adresse</Label>
                            <Input
                                value={form.adresse}
                                onChange={(e) => setForm((p) => ({ ...p, adresse: e.target.value }))}
                                placeholder="Adresse de la copropriété"
                                className="h-10 rounded-sm mt-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Code postal</Label>
                                <Input
                                    value={form.code_postal}
                                    onChange={(e) => setForm((p) => ({ ...p, code_postal: e.target.value }))}
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Ville</Label>
                                <Input
                                    value={form.ville}
                                    onChange={(e) => setForm((p) => ({ ...p, ville: e.target.value }))}
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Nature du projet</Label>
                            <Input
                                value={form.nature_projet}
                                onChange={(e) => setForm((p) => ({ ...p, nature_projet: e.target.value }))}
                                placeholder="Ex : ravalement façade, rénovation parties communes..."
                                className="h-10 rounded-sm mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                        <Button
                            onClick={handleCreate}
                            disabled={submitting}
                            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                            data-testid="copro-create-btn"
                        >
                            {submitting ? 'Création...' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
